import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as PR from "../../../prime-react/index";
import "./Analytics.scss";
import Layout from '../../../layouts/Layout';
import AnalyticsGraph from './AnalyticsGraph';
import { analystChartTitles, areDateRangesEqual, defaultDateRange, defaultPlayerDateRange } from '../../../utils/reuse';
import { useSelector } from 'react-redux';
import { useToast } from '../../../context/ToastContext';
import ReportsSentRegulators from './ReportsSentRegulators';
import SuspiciousReport from './SuspiciousReport';
import { inputSearchHandler } from '../../../utils/inputValidate';

const AnalystAnalyticsDetails = () => {
    const { state } = useLocation();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const isExport = useSelector(state => state.auth?.exportPermission);
    const { graphType, graphColor, secondGraphColor, optionsData, memberOptions, selectedDates, placeholder, responseKey, graphCountKey, secondGraphCountKey, graphNameKey, selectedValuesData, selectedRegulator, selectedMember, selectedSGB, regulators, governingBodies, alertID, selectedLocationsData, selectedCustomerLocationsData, selectedCustomerStatesData, locations } = state ?? {};
    const [selectedValues, setSelectedValues] = useState(selectedValuesData);
    const [selectedRegulatorValues, setSelectedRegulatorValues] = useState(selectedRegulator);
    const [selectedSGBValues, setSelectedSGBValues] = useState(selectedSGB);
    const [selectedMemberValue, setSelectedMember] = useState(selectedMember);
    const [dates, setDates] = useState(selectedDates);
    const [visible, setVisible] = useState(false);
    const [searchAlertId, setSearchAlertId] = useState(alertID);
    const [id, setAlertID] = useState(alertID);
    const [error, setError] = useState('');
    const [type, setType] = useState('');
    const [offset, setoffset] = useState(0);
    const [page, setPage] = useState(1);
    const [apiResponseData, setApiResponseData] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState(selectedLocationsData || []);
    const [selectedCustomerLocations, setSelectedCustomerLocations] = useState(selectedCustomerLocationsData || []);
    const [selectedCustomerStates, setSelectedCustomerStates] = useState(selectedCustomerStatesData || []);

    const customerStatesOptions = useMemo(() => {
        if (!selectedCustomerLocations || selectedCustomerLocations.length === 0 || !locations) {
            return [];
        }
        const locationIds = selectedCustomerLocations.map(loc => loc.id);
        const states = locations
            .filter(loc => locationIds.includes(loc.id) && loc.states)
            .flatMap(loc => loc.states)
            .map(stateName => ({ id: stateName, name: stateName }));
        const uniqueStates = [];
        const seen = new Set();
        for (const s of states) {
            if (!seen.has(s.id)) {
                seen.add(s.id);
                uniqueStates.push(s);
            }
        }
        return uniqueStates.sort((a, b) => a.name.localeCompare(b.name));
    }, [selectedCustomerLocations, locations]);

    useEffect(() => {
        if (selectedCustomerStates?.length > 0) {
            const validStateIds = new Set(customerStatesOptions.map(s => s.id));
            const updatedStates = selectedCustomerStates.filter(s => validStateIds.has(s.id));
            if (updatedStates.length !== selectedCustomerStates.length) {
                setSelectedCustomerStates(updatedStates);
            }
        }
    }, [customerStatesOptions, selectedCustomerStates]);
    const setExportModalHandler = (value) => {
        setVisible(value);
    }
    const searchHandler = e => {
        e?.preventDefault()
        if (!searchAlertId) return;
        let alertID = searchAlertId;
        if (!searchAlertId.toLowerCase().startsWith('alert-')) {
            alertID = `alert-${searchAlertId}`
        }
        setAlertID(alertID)
    }
    useEffect(() => {
        if(!graphType){
            navigate('/my-analytics');
            return;
        }
    }, [graphType, navigate]);
    const getDataHandler = (error, type = '') => {
        setError(error)
        setType(type);
        setTimeout(() => {
            setError('');
            setType('')
        }, 3000);

    }
    const multiSelectChangeHandler = (value, setter) => {
        setter(value);
        setPaginationHandler(0, 1)
    }
    const setPaginationHandler = (o, p) => {
        setoffset(o);
        setPage(p); 
    }
    const getResponseHandler = useCallback((data) => {
       setApiResponseData(data);
    }, []);
    const isDefaultDateRange = (graphType === 'pcps' || graphType === 'mcps') ? areDateRangesEqual(dates, defaultPlayerDateRange) : areDateRangesEqual(dates, defaultDateRange);
    const handleClearDates = useCallback(() => {
        setDates((graphType === 'pcps' || graphType === 'mcps') ? defaultPlayerDateRange : defaultDateRange);
    }, [graphType]);
    
    return (
        <>
            <Layout>
                <div className='menu-section'>
                    <Link to="/my-analytics" className='back-link'><i className="pi pi-arrow-left"></i> Back to Analytics</Link>
                </div>
                <section className={(graphType !== 'rspr' && graphType !== 'srpa') ? "my-analytics-tab" : "my-analytics-tab reports-sent-regulators w-12"}>
                    <div className="flex align-items-center justify-content-between gap-3">
                        <div className='heading-card'>
                            <h1>{analystChartTitles[graphType]?.title}</h1>
                            <p>{analystChartTitles[graphType]?.description}</p>
                        </div>
                        <div className="header-right flex gap-2 flex-wrap">
                            {graphType !== 'srpa' &&
                                <div className='date custom-clear-calendar'>
                                    <PR.Calendar
                                        value={dates}
                                        onChange={(e) => setDates(e.value)}
                                        selectionMode="range"
                                        iconPos="left"
                                        readOnlyInput
                                        showIcon
                                        className='date-calendar'
                                        placeholder='Date'
                                        dateFormat="dd/mm/yy"
                                        maxDate={new Date()}
                                    />
                                    {!isDefaultDateRange &&
                                        <div className='custom-clear'>
                                            <i className="pi pi-times text-sm" onClick={handleClearDates}></i>
                                        </div>
                                    }
                                </div>
                            }
                            {graphType === 'arps' && (
                                <div className="flex flex-row gap-3">
                                    <PR.Dropdown
                                        value={selectedMemberValue}
                                        onChange={(e) => setSelectedMember(e.value)}
                                        options={memberOptions}
                                        optionLabel="name"
                                        placeholder="Select Member"
                                        showClear
                                        className="multi-select-dropdown"
                                    />
                                </div>
                            )}

                            {graphType === 'rspr' &&
                                <div className="flex flex-row gap-3">
                                    <PR.MultiSelect
                                        filter
                                        value={selectedRegulatorValues} onChange={(e) => multiSelectChangeHandler(e.value, setSelectedRegulatorValues)}
                                        options={regulators}
                                        placeholder="Select Regulator"
                                        maxSelectedLabels={0}
                                        className="multi-select-dropdown"
                                        resetFilterOnHide
                                        showClear
                                    />
                                    <PR.MultiSelect
                                        filter
                                        value={selectedSGBValues} onChange={(e) => multiSelectChangeHandler(e.value, setSelectedSGBValues)}
                                        options={governingBodies}
                                        placeholder="Select SGB"
                                        maxSelectedLabels={0}
                                        className="multi-select-dropdown"
                                        resetFilterOnHide
                                        showClear
                                    />
                                </div>
                            }
                            {placeholder &&
                                <div className="flex flex-row gap-3">
                                    <PR.MultiSelect
                                        filter
                                        value={selectedValues}
                                        showSelectAll={optionsData?.length <= 15}
                                        onChange={(e) => {
                                            const newValue = e.value || [];
 
                                            if (newValue.length > 15) {
                                                showToast('warn', 'You can select a maximum of 15 items');
                                                return;
                                            }
                                            setSelectedValues(newValue);
                                        }}
                                        options={optionsData}
                                        optionLabel="name"
                                        placeholder={placeholder}
                                        maxSelectedLabels={0}
                                        className="multi-select-dropdown"
                                        resetFilterOnHide
                                        showClear
                                        pt={{
                                            checkboxContainer: {
                                                onClick: (e) => {
                                                    e.stopPropagation();
                                                    const parent = e.target?.parentNode;
                                                    if (parent && typeof parent.click === 'function') {
                                                        parent.click();
                                                    }
                                                },
                                            },
                                        }}
                                    />
                                </div>
                            }
                            {graphType === 'mcps' && (
                                <>
                                    <div className="flex flex-row gap-3">
                                        <PR.MultiSelect
                                            filter
                                            value={selectedLocations}
                                            showSelectAll={locations?.length <= 15}
                                            onChange={(e) => {
                                                const newValue = e.value || [];
                                                if (newValue.length > 15) {
                                                    showToast('warn', 'You can select a maximum of 15 locations');
                                                    return;
                                                }
                                                setSelectedLocations(newValue);
                                            }}
                                            options={locations}
                                            optionLabel="name"
                                            placeholder="Select Event Locations"
                                            maxSelectedLabels={0}
                                            className="multi-select-dropdown"
                                            resetFilterOnHide
                                            showClear
                                            pt={{
                                                checkboxContainer: {
                                                    onClick: (e) => {
                                                        e.stopPropagation();
                                                        const parent = e.target?.parentNode;
                                                        if (parent && typeof parent.click === 'function') {
                                                            parent.click();
                                                        }
                                                    },
                                                },
                                            }}
                                        />
                                    </div>
                                    <div className="flex flex-row gap-3">
                                        <PR.MultiSelect
                                            filter
                                            value={selectedCustomerLocations}
                                            showSelectAll={locations?.length <= 15}
                                            onChange={(e) => {
                                                const newValue = e.value || [];
                                                if (newValue.length > 15) {
                                                    showToast('warn', 'You can select a maximum of 15 customer locations');
                                                    return;
                                                }
                                                setSelectedCustomerLocations(newValue);
                                            }}
                                            options={locations}
                                            optionLabel="name"
                                            placeholder="Select Customer Locations"
                                            maxSelectedLabels={0}
                                            className="multi-select-dropdown"
                                            resetFilterOnHide
                                            showClear
                                            pt={{
                                                checkboxContainer: {
                                                    onClick: (e) => {
                                                        e.stopPropagation();
                                                        const parent = e.target?.parentNode;
                                                        if (parent && typeof parent.click === 'function') {
                                                            parent.click();
                                                        }
                                                    },
                                                },
                                            }}
                                        />
                                    </div>
                                    <div className="flex flex-row gap-3">
                                        <PR.MultiSelect
                                            filter
                                            value={selectedCustomerStates}
                                            showSelectAll={customerStatesOptions?.length <= 15}
                                            onChange={(e) => {
                                                const newValue = e.value || [];
                                                if (newValue.length > 15) {
                                                    showToast('warn', 'You can select a maximum of 15 customer states');
                                                    return;
                                                }
                                                setSelectedCustomerStates(newValue);
                                            }}
                                            options={customerStatesOptions}
                                            optionLabel="name"
                                            placeholder="Select Customer States"
                                            maxSelectedLabels={0}
                                            disabled={!customerStatesOptions.length}
                                            className="multi-select-dropdown customer-state-dropdown"
                                            resetFilterOnHide
                                            showClear
                                            pt={{
                                                checkboxContainer: {
                                                    onClick: (e) => {
                                                        e.stopPropagation();
                                                        const parent = e.target?.parentNode;
                                                        if (parent && typeof parent.click === 'function') {
                                                            parent.click();
                                                        }
                                                    },
                                                },
                                            }}
                                        />
                                    </div>
                                </>
                            )}
                            {graphType === 'srpa' &&
                                <div className='p-input-icon-right'>
                                    <span className="p-input-icon-left w-12">
                                        <i className="pi pi-search"></i>
                                        <form onSubmit={searchHandler}>
                                            <PR.InputText
                                                id="alertLegacyId"
                                                placeholder="Enter Alert-ID"
                                                className="search-box"
                                                value={searchAlertId}
                                                autoComplete="off"
                                                onChange={(event) => {
                                                    inputSearchHandler(event, setSearchAlertId)
                                                }}
                                            />
                                        </form>
                                        {(!type && error) && <p className="error-message mt-1 absolute">{error}</p>}
                                        {(type && error) && <p className="error-message mt-1 absolute">{'No results found'}</p>}
                                    </span>
                                    <span className="right-clear-icon">
                                        {!!searchAlertId && <i style={{ cursor: 'pointer', color: "#6c757d" }} onClick={() => { inputSearchHandler({ target: { value: "" } }, setSearchAlertId); }} className="pi pi-times text-sm mr-2" />}
                                    </span>
                                </div>
                            }
                            <div className="export-block">
                                <PR.Button label="Export" disabled={!isExport || !apiResponseData?.length} onClick={() => { setVisible(true) }} icon="pi pi-upload" className="export-button" />
                            </div>
                        </div>
                    </div>
                    {graphType && graphType === 'rspr' && <ReportsSentRegulators selectedDates={dates} regulatorsData={selectedRegulatorValues} sgbData={selectedSGBValues} isDetailScreen={true} exportVisible={visible} setExportModalHandler={setExportModalHandler} offsetvalue={offset} pageValue={page} setPaginationHandler={setPaginationHandler} getResponseHandler={getResponseHandler}></ReportsSentRegulators>}
                    {graphType && graphType === 'srpa' && <SuspiciousReport isDetailScreen={true} alertId={id} exportVisible={visible} setExportModalHandler={setExportModalHandler} getDataHandler={getDataHandler} getResponseHandler={getResponseHandler}></SuspiciousReport>}
                    {(graphType && graphType !== 'rspr' && graphType !== 'srpa') && <AnalyticsGraph graphType={graphType} graphColor={graphColor} secondGraphColor={secondGraphColor} responseKey={responseKey} graphCountKey={graphCountKey} secondGraphCountKey={secondGraphCountKey} graphNameKey={graphNameKey} optionsData={optionsData} placeholder={placeholder} dates={dates} isDetailScreen={true} exportVisible={visible} filteredValues={selectedValues} filteredMember={selectedMemberValue} setExportModalHandler={setExportModalHandler} getResponseHandler={getResponseHandler} locations={locations} selectedLocationsData={selectedLocations} selectedCustomerLocationsData={selectedCustomerLocations} selectedCustomerStatesData={selectedCustomerStates}></AnalyticsGraph>
                    }
                </section>
            </Layout>

        </>
    );
};

export default AnalystAnalyticsDetails;